/* eslint-disable no-unused-vars */
import React, { useCallback } from 'react';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Row from './Row';
import ProductRow from './ProductRow';
import TariffRow from './TariffRow';
import PaginationJump from '../common/PaginationJump';
import ScreeningProgressNote from './ScreeningProgressNote';

interface ScreeningProgress {
  chunksProcessed: number;
  chunksTotal: number;
  progressPercentage: number;
}

interface ScreeningFailureWarning {
  show: boolean;
  message: string;
  errorCode?: string;
  failedRecordIds?: string[];
}

interface TableType {
  cols: React.ReactNode;
  suppliersData: any[]; // Changed to be a required array
  location?: string;
  selectedItems: any[];
  setSelectedItems: (item: any) => void;
  screeningHistory?: any; // Added this prop back for other components
  customRowComponent?: (props: any) => React.ReactNode;
  loading: boolean;
  handleEdit?: (item: any) => void;
  handleDelete?: (item: any) => void;
  viewType?: string;
  isScreeningInProgress?: boolean;
  aiInProgress?: boolean;
  screeningProgress?: ScreeningProgress | null;
  screeningFailureWarning?: ScreeningFailureWarning | null;
  currentPage: number;
  totalPages: number;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}

const MasterTable: React.FC<TableType> = ({
  cols,
  suppliersData,
  location,
  selectedItems,
  setSelectedItems,
  customRowComponent,
  loading,
  handleEdit,
  handleDelete,
  viewType,
  isScreeningInProgress = false,
  aiInProgress = false,
  screeningProgress,
  screeningFailureWarning = null,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const handleJump = useCallback(
    (pageNumber: number) => {
      if (onPageChange) {
        onPageChange({} as React.ChangeEvent<unknown>, pageNumber);
      }
    },
    [onPageChange],
  );

  return (
    <div style={{ width: '100%', position: 'relative', zIndex: 0 }}>
      <div className='screening-blur-container' style={{ width: '100%' }}>
        <div className='library-cont' style={{ marginTop: '0px' }}>
          <div className='screening-results-container'>
            {cols}
            <div className='screening-results-table-container-no-hieght'>
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '445px',
                    width: '100%',
                  }}
                >
                  <CircularProgress color='primary' />
                </div>
              ) : suppliersData.length === 0 ? (
                <div
                  className='audit-dashboard-cards-no-ticket'
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '445px',
                    width: '100%',
                  }}
                >
                  {isScreeningInProgress ? '' : 'No Items Found'}
                </div>
              ) : (
                suppliersData.map((row: any) =>
                  customRowComponent ? (
                    <React.Fragment key={row.id}>
                      {customRowComponent({
                        supplier: row,
                        transaction: row,
                        location: location,
                        selectedItems: selectedItems,
                        setSelectedItems: setSelectedItems,
                        aiInProgress: aiInProgress,
                        screeningFailureWarning: screeningFailureWarning,
                      })}
                    </React.Fragment>
                  ) : location === '/tariff' ? (
                    <TariffRow
                      key={row.id}
                      transaction={row}
                      location={location}
                      viewType={viewType}
                      selectedItems={selectedItems}
                      setSelectedItems={setSelectedItems}
                      handleEdit={handleEdit}
                      handleDelete={handleDelete}
                    />
                  ) : location === '/product' ? (
                    <ProductRow
                      key={row.id}
                      transaction={row}
                      location={location}
                      viewType={viewType}
                      selectedItems={selectedItems}
                      setSelectedItems={setSelectedItems}
                      handleEdit={handleEdit}
                      handleDelete={handleDelete}
                    />
                  ) : (
                    <Row
                      key={row.id}
                      supplier={row}
                      location={location}
                      viewType={viewType}
                      selectedItems={selectedItems}
                      setSelectedItems={setSelectedItems}
                      handleEdit={handleEdit}
                      handleDelete={handleDelete}
                      aiInProgress={aiInProgress}
                      screeningFailureWarning={screeningFailureWarning}
                    />
                  ),
                )
              )}
            </div>
            {suppliersData.length > 0 && (
              <div className='screening-results-footer'>
                <div className='screening-results-pagination-text'>
                  Page {currentPage} of {totalPages}
                </div>
                <div className='screening-results-footer-action-group'>
                  <div
                    className='hb-pagination'
                    style={{
                      gap: '16px',
                      flexWrap: 'wrap',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Pagination
                      onChange={onPageChange}
                      size='large'
                      count={totalPages}
                      shape='rounded'
                      variant='text'
                      page={currentPage}
                      disabled={totalPages <= 1}
                    />
                    <PaginationJump
                      totalPages={totalPages}
                      currentPage={currentPage}
                      onJump={handleJump}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`screening-overlay ${
          isScreeningInProgress ? 'visible' : ''
        }`}
      >
        <div className='screening-message'>
          <h3>
            {viewType === 'Tariff'
              ? 'Calculations in Progress...'
              : 'Screen in Progress...'}
          </h3>
          <ScreeningProgressNote progress={screeningProgress} />
        </div>
      </div>
    </div>
  );
};

export default MasterTable;
